'use client';

import customFetch, { removeSessionTokens, setSessionTokens } from '@/api/fetch';
import { useTranslation } from '@/app/i18n/client';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useToast } from './molecules';

export function AuthenticatedUrlHandler() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const { lng } = useParams() as { lng: string };
  const { t } = useTranslation(lng, ['errors']);
  const { showToast } = useToast();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const hasProcessedRef = useRef(false);

  const authToken = searchParams?.get('authToken');

  useEffect(() => {
    if (!authToken || hasProcessedRef.current) return;

    if (pathname?.includes('/admin')) return;

    hasProcessedRef.current = true;

    const authenticate = async () => {
      setIsAuthenticating(true);

      try {
        const response = await customFetch('/authenticated-url/validate', {
          method: 'POST',
          body: JSON.stringify({ token: authToken }),
        });

        removeSessionTokens();
        setSessionTokens({
          accessToken: response._clientToken || response.token?.idToken || '',
          refreshToken: '',
        });

        const newParams = new URLSearchParams(searchParams?.toString() || '');
        newParams.delete('authToken');
        const newUrl = newParams.toString() ? `${pathname}?${newParams.toString()}` : pathname || '/';
        window.location.replace(newUrl);
      } catch (error: any) {
        const errorCode = error?.code;

        showToast({
          title: t('errors:errorTitle'),
          description: t(`errors:${errorCode}`),
          variant: 'error',
          duration: 4000,
        });

        const newParams = new URLSearchParams(searchParams?.toString() || '');
        newParams.delete('authToken');
        router.replace(`${pathname}?${newParams.toString()}`);
      } finally {
        setIsAuthenticating(false);
      }
    };

    authenticate();
  }, [authToken]);

  if (!isAuthenticating) return null;

  return (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-white/80 backdrop-blur-sm'>
      <div className='flex flex-col items-center gap-4'>
        <div className='h-10 w-10 animate-spin rounded-full border-4 border-neutral-300 border-t-black' />
        <p className='text-sm font-medium text-neutral-600'>Autenticando...</p>
      </div>
    </div>
  );
}
